/**
 * Combobox inline-create — reveal-variant "Add new" flow for SeeMe comboboxes.
 *
 * Generalized from the pm_crm owner/affiliate inline-create patterns so any
 * combobox can offer a mini-form create without cloning the wiring.
 *
 * Combobox markup (in addition to the standard data-combobox structure):
 *   <div data-combobox
 *        data-inline-create-target="<id of hidden mini-form div>"
 *        data-inline-create-event="entityCreated">
 *
 * Mini-form container (sibling of the combobox, hidden by default):
 *   <div id="<id>" data-inline-create-form style="display:none;">
 *     <div data-inline-create-errors></div>
 *     <input name="..." data-inline-create-name>   ← prefilled with the search query
 *     ...more inputs...
 *     <button hx-post="<create-url>" hx-include="#<id>" hx-swap="none">Create</button>
 *     <button data-inline-create-cancel>Cancel</button>
 *   </div>
 *
 * Flow:
 *   combobox:add → hide the combobox, reveal the mini-form, prefill the
 *   name input with the search query. The create endpoint responds with
 *   HX-Trigger {"<event>": {"id": .., "name": .., "value"?: ..}, "showToast": {...}}
 *   (the optional value fills `data-inline-create-fill`, below) — the event
 *   handler injects + selects the new option, resets and hides the mini-form,
 *   and restores the combobox. A 400 or 403 response puts each
 *   field error under its input (.is-invalid); errors on no rendered field go
 *   in [data-inline-create-errors].
 *
 * Inside a dropdown menu (the partner sheet's Preferred Title Company):
 *   the mini-form is itself a `data-menu-panel` of the same menu (`data-inline-create-form` +
 *   `data-menu-panel="create"`, `id` = the host's target).  The dropdown's own panel switcher shows
 *   and hides it, so this module leaves `hidden` and the host alone; "Add new..." carries
 *   `data-show-panel="create"` beside `data-inline-create-add`, and Back carries
 *   `data-show-panel="main"` beside `data-inline-create-cancel`.  On success the pick lands in the
 *   host and the Back control is clicked, returning to the panel that holds the host.
 *
 * URL-backed typeahead host (same contract, no option list):
 *   A directory too large to render as combobox options (the buyer directory
 *   is ~6k rows) stays a server-search `.typeahead[data-typeahead-url]`. It
 *   carries the same `data-inline-create-target` / `data-inline-create-event`
 *   attributes and adds an "Add new..." footer inside `.typeahead-panel`:
 *
 *     <div class="typeahead" data-typeahead-url="/search/"
 *          data-typeahead-target="field_name"
 *          data-inline-create-target="<id>"
 *          data-inline-create-event="entityCreated"
 *          data-inline-create-fill="other_field_name">
 *       <input class="form-control typeahead-input" required>
 *       <div class="typeahead-panel">
 *         <div class="typeahead-results"></div>
 *         <hr class="dropdown-divider">
 *         <button type="button" class="dropdown-item typeahead-footer"
 *                 data-inline-create-add>Add new...</button>
 *       </div>
 *       <input type="hidden" name="field_name">
 *     </div>
 *
 *   On the created event the visible input shows the new name, the hidden
 *   `data-typeahead-target` input holds the new id, `typeahead:select` fires
 *   so sheet-level listeners see it, and `data-inline-create-fill` (optional)
 *   names one more form input to fill with the event's `value` (else the new
 *   name) when it is still empty (always, with
 *   `data-inline-create-fill-replace`). `required` on the host's inputs is stashed while the host is hidden — Chrome refuses to
 *   submit a form containing a required control inside a `display:none`
 *   subtree.
 *
 * A `<form data-inline-create-form>` (the owner's Link to buyer sheet, `New buyer…`) uses only the
 * error rendering: a 400 or 403 from the form's own post puts each field error under its input.
 * Enter in it submits the form natively; there is no parent form to protect.
 */

/**
 * Select a value in a combobox, injecting the option if it isn't in the
 * list yet. Mirrors combobox.js selectOption display/state handling.
 * Shared by the inline-create flows (also used by pm-crm-create.js).
 */
export function selectComboboxOption(combobox, value, label) {
    var list = combobox.querySelector('[data-combobox-list]');
    var opt = list
        ? list.querySelector('[data-combobox-option="' + CSS.escape(String(value)) + '"]')
        : null;
    if (list && !opt) {
        opt = document.createElement('a');
        opt.className = 'dropdown-item';
        opt.href = '#';
        opt.setAttribute('data-combobox-option', value);
        opt.textContent = label;
        list.appendChild(opt);
    }

    var hiddenInput = combobox.querySelector('[data-combobox-input]');
    if (hiddenInput) hiddenInput.value = value;

    var valueEl = combobox.querySelector('[data-combobox-value]');
    if (valueEl) {
        valueEl.textContent = label;
        valueEl.classList.remove('combobox-value--placeholder');
    }

    combobox.querySelectorAll('[data-combobox-option]').forEach(function (el) {
        el.removeAttribute('aria-selected');
    });
    if (opt) opt.setAttribute('aria-selected', 'true');

    var menu = combobox.querySelector('.dropdown-menu');
    if (menu) menu.classList.remove('show');
}

/**
 * Select a value on a URL-backed `.typeahead` host: visible input shows the
 * label, the `data-typeahead-target` hidden input holds the id, results close,
 * and `typeahead:select` fires so sheet controllers see it like a real pick.
 * Mirrors `typeaheadFormSelect` in `static/js/seeme/typeahead.js`.
 */
export function selectTypeaheadOption(host, value, label, fillValue) {
    var scope = host.closest('form') || host;
    var targetName = host.getAttribute('data-typeahead-target');
    if (targetName) {
        var hidden = scope.querySelector('input[name="' + targetName + '"]');
        if (hidden) hidden.value = value;
    }
    var input = host.querySelector('.typeahead-input');
    if (input) input.value = label;
    var clearBtn = host.querySelector('.typeahead-clear-btn');
    if (clearBtn) clearBtn.style.display = '';
    var results = host.querySelector('.typeahead-results');
    if (results) {
        results.innerHTML = '';
        results.classList.remove('show');
    }
    host.dispatchEvent(new CustomEvent('typeahead:select', {
        bubbles: true,
        detail: {id: value, name: label, value: fillValue || label},
    }));
}

function getHost(formEl) {
    return document.querySelector('[data-inline-create-target="' + formEl.id + '"]');
}

function isComboboxHost(host) {
    return host.hasAttribute('data-combobox');
}

// A `required` control inside a `display:none` subtree makes Chrome refuse the
// submit ("not focusable") with nothing visible to fix, so the attribute is
// parked on the element while its host is hidden and put back on restore.
function stashRequired(host, hidden) {
    host.querySelectorAll('input[required], input[data-inline-create-required]').forEach(function (el) {
        if (hidden) {
            el.removeAttribute('required');
            el.setAttribute('data-inline-create-required', '');
        } else if (el.hasAttribute('data-inline-create-required')) {
            el.removeAttribute('data-inline-create-required');
            el.setAttribute('required', '');
        }
    });
}

function clearErrors(formEl) {
    var box = formEl.querySelector('[data-inline-create-errors]');
    if (box) box.innerHTML = '';
    formEl.querySelectorAll('[data-inline-create-field-error]').forEach(function (el) { el.remove(); });
    formEl.querySelectorAll('.is-invalid').forEach(function (el) { el.classList.remove('is-invalid'); });
}

// Errors on a field the form renders go under that field; the rest (403, __all__) go in the top box.
function renderErrors(formEl, responseText) {
    var box = formEl.querySelector('[data-inline-create-errors]');
    if (!box) return;
    clearErrors(formEl);
    var general = [];
    try {
        var data = JSON.parse(responseText);
        var fields = data.errors || data;
        Object.keys(fields).forEach(function (key) {
            var val = fields[key];
            var messages = [];
            (Array.isArray(val) ? val : [val]).forEach(function (item) {
                var msg = (item && item.message) || item;
                if (typeof msg === 'string') messages.push(msg);
            });
            var input = formEl.querySelector('[name="' + key + '"]');
            if (!input || input.type === 'hidden') {
                general = general.concat(messages);
                return;
            }
            input.classList.add('is-invalid');
            var anchor = input.closest('.input-group') || input;
            messages.forEach(function (msg) {
                var div = document.createElement('div');
                div.className = 'invalid-feedback d-block';
                div.setAttribute('data-inline-create-field-error', '');
                div.textContent = msg;
                anchor.insertAdjacentElement('afterend', div);
                anchor = div;
            });
        });
    } catch (err) {
        // Non-JSON 400 body — fall through to the generic message.
    }
    if (!general.length && !formEl.querySelector('[data-inline-create-field-error]')) {
        general = ['Could not create — check the fields and try again.'];
    }
    general.forEach(function (msg) {
        var div = document.createElement('div');
        div.className = 'text-error fs-sm';
        div.textContent = msg;
        box.appendChild(div);
    });
}

function toggleInlineCreate(host, formEl, show, query) {
    // New callers use the native hidden attribute; retain the existing display-based callers.
    // A menu panel is shown and hidden by the dropdown's own panel switcher, which clears `display`
    // only, and the host stays put: it lives in the sibling panel, which that switcher hides.
    var inPanel = formEl.hasAttribute('data-menu-panel');
    if (!inPanel) formEl.hidden = !show;
    // Opt-in controls must not block or join the parent form while their card is closed.
    formEl.querySelectorAll('[data-inline-create-control]').forEach(function (el) {
        el.disabled = !show;
    });
    if (show) {
        var nameInput = formEl.querySelector('[data-inline-create-name]');
        if (nameInput) nameInput.value = query || '';
        clearErrors(formEl);
        stashRequired(host, true);
        if (!inPanel) host.style.display = 'none';
        formEl.style.display = '';
        if (nameInput) nameInput.focus();
    } else {
        formEl.style.display = 'none';
        if (!inPanel) host.style.display = '';
        stashRequired(host, false);
    }
}

// Per-entity HX-Trigger created events (e.g. titleCompanyCreated) are
// registered lazily on first reveal — the names live in markup, so they
// can't be known at module load.
var registeredEvents = {};

function ensureCreatedListener(eventName) {
    if (!eventName || registeredEvents[eventName]) return;
    registeredEvents[eventName] = true;
    document.body.addEventListener(eventName, function (e) {
        var formEl = e.target.closest ? e.target.closest('[data-inline-create-form]') : null;
        if (!formEl) return;
        var combobox = getHost(formEl);
        if (!combobox || combobox.getAttribute('data-inline-create-event') !== eventName) return;
        var d = e.detail || {};
        if (d.id == null) return;
        var label = d.name || '';
        var fillValue = d.value || label;
        if (isComboboxHost(combobox)) {
            selectComboboxOption(combobox, d.id, label);
        } else {
            selectTypeaheadOption(combobox, d.id, label, fillValue);
        }
        // Hidden inputs carry fixed values (the market, the extra event) the next create still needs.
        formEl.querySelectorAll('input:not([type="hidden"])').forEach(function (el) { el.value = ''; });
        toggleInlineCreate(combobox, formEl, false);
        fillCompanionField(combobox, fillValue);
        // Inside a dropdown, go back to the panel that holds the pick.
        var back = formEl.hasAttribute('data-menu-panel') && formEl.querySelector('[data-show-panel]');
        if (back) back.click();
    });
}

/**
 * `data-inline-create-fill` names one more input in the surrounding form that
 * should take the picked record's fill value — the event's `value`, else its
 * name.  The assignment sheet's `buyer_legal_name` takes the buyer's name
 * (`buyer.name`), not the title, which can also list its owners.  By
 * default it only fills an empty field: on Create, a legal name the rep
 * already typed is the authoritative one.  `data-inline-create-fill-replace`
 * always overwrites it instead — on Edit the field holds the previous buyer's
 * legal name, which a new buyer must not keep.
 */
function fillCompanionField(host, value) {
    var fillName = host.getAttribute('data-inline-create-fill');
    if (!fillName || !value) return;
    var scope = host.closest('form') || document;
    var field = scope.querySelector('[name="' + fillName + '"]');
    if (!field) return;
    if (host.hasAttribute('data-inline-create-fill-replace') || !field.value.trim()) field.value = value;
}

// A search pick fills the companion field the same way an inline create does,
// so the two ways of choosing a buyer leave the sheet in the same state.
document.addEventListener('typeahead:select', function (e) {
    var host = e.target.closest && e.target.closest('[data-inline-create-fill]');
    if (!host || !e.detail) return;
    fillCompanionField(host, e.detail.value || e.detail.name);
});

// "Add new..." on a URL-backed typeahead → reveal the mini-form, prefilled
// with whatever the rep had typed into the search box.
document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-inline-create-add]');
    if (!btn) return;
    e.preventDefault();
    var host = btn.closest('[data-inline-create-target]');
    if (!host) return;
    var formEl = document.getElementById(host.getAttribute('data-inline-create-target'));
    if (!formEl) return;
    ensureCreatedListener(host.getAttribute('data-inline-create-event'));
    var input = host.querySelector('.typeahead-input');
    var targetName = host.getAttribute('data-typeahead-target');
    var scope = host.closest('form') || host;
    var selected = targetName ? scope.querySelector('input[name="' + targetName + '"]') : null;
    // A selected label describes an existing record, not a proposed new name.
    var query = selected && selected.value ? '' : (input ? input.value.trim() : '');
    toggleInlineCreate(host, formEl, true, query);
});

// "Add new..." in the combobox → reveal the mini-form
document.addEventListener('combobox:add', function (e) {
    var combobox = e.target.closest('[data-combobox]');
    if (!combobox) return;
    var targetId = combobox.getAttribute('data-inline-create-target');
    if (!targetId) return;
    var formEl = document.getElementById(targetId);
    if (!formEl) return;
    ensureCreatedListener(combobox.getAttribute('data-inline-create-event'));
    toggleInlineCreate(combobox, formEl, true, (e.detail.query || '').trim());
});

// Enter inside a mini-form input → click the save button instead of bubbling to the parent form
document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var formEl = e.target.closest && e.target.closest('[data-inline-create-form]');
    if (!formEl || formEl.tagName === 'FORM' || e.target.tagName !== 'INPUT') return;
    e.preventDefault();
    var saveBtn = formEl.querySelector('[hx-post]');
    if (saveBtn) saveBtn.click();
});

// Cancel → hide the mini-form, restore the combobox
document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-inline-create-cancel]');
    if (!btn) return;
    var formEl = btn.closest('[data-inline-create-form]');
    var combobox = formEl && getHost(formEl);
    if (!combobox) return;
    toggleInlineCreate(combobox, formEl, false);
});

// 400 validation failures → render field errors inline
document.body.addEventListener('htmx:afterRequest', function (e) {
    var el = e.detail.elt;
    var formEl = el.closest && el.closest('[data-inline-create-form]');
    if (!formEl) return;
    if (e.detail.successful) {
        clearErrors(formEl);
        return;
    }
    if (e.detail.xhr && (e.detail.xhr.status === 400 || e.detail.xhr.status === 403)) {
        renderErrors(formEl, e.detail.xhr.responseText);
    }
});
